@import url("../../semantic/color.css");
@import url("../../semantic/typography.css");
@import url("../../semantic/spacing.css");
@import url("../../semantic/sizing.css");
@import url("../../semantic/layering.css");
@import url("../../semantic/motion.css");

/* Keycap-styled button with four semantic actionRole variants.
 * The "dish" that gives pseudo-skeuomorphic depth is a pure-CSS pseudo-element (concentric inset radial-gradient).
 * Only direct children translate on press so icon + label move together while the dish stays put. */

.doss-button {
  position: relative;
  isolation: isolate;
  min-height: var(--form-element-height);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--icon-margin);
  padding: var(--action-padding-block) var(--action-padding-inline);
  border-radius: var(--action-radius);
  font-size: var(--action-text-size);
  font-weight: var(--action-font-weight);
  line-height: var(--action-line-height);
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  user-select: none;
  transition:
    background-color var(--action-transition-duration) ease,
    background-image var(--action-transition-duration) ease,
    border-color var(--action-transition-duration) ease,
    color var(--action-transition-duration) ease,
    box-shadow var(--action-release-duration) ease,
    transform var(--action-release-duration) ease;

  &:active:not([aria-disabled="true"]),
  &.loading {
    transition-duration: var(--action-press-duration);
  }

  /* Disabled — uniform opacity across all roles.
   * The aria-busy guards keep a loading button out of this rule: while loading, the .doss-loadable
   * primitive dims only the content (label/icon), leaving the keycap chrome and glow at full strength —
   * an element-level dim here would stack on top of that.
   *
   * Note: `pointer-events: none` is intentionally NOT set,
   * so a wrapping `<Tooltip>` receives :hover / :focus-visible on the trigger.
   * Click events are guarded in the React handler instead. */
  &[aria-disabled="true"]:not([aria-busy="true"]),
  &.disabled:not([aria-busy="true"]),
  &:disabled {
    cursor: not-allowed;
    opacity: var(--disabled-opacity);
  }

  /* Dish — concentric inset radial-gradient. */
  &::before {
    content: "";
    position: absolute;
    z-index: 0;
    inset: var(--dish-inset);
    border-radius: calc(var(--action-radius) - var(--dish-inset));
    pointer-events: none;
  }

  /* Direct children sit above the dish, and translate as one unit on press.
   * The pseudo-elements don't match `> *` so they stay put. */
  & > * {
    position: relative;
    z-index: 1;
    transition: transform var(--action-release-duration) ease;
  }

  /* A leading icon splits the leading inline padding: it sits at base − --icon-margin,
   * and the gap after it (above) is --icon-margin. Icon-only tightens both edges symmetrically. */
  &:has(> .doss-icon) {
    padding-inline-start: calc(var(--action-padding-inline) - var(--icon-margin));
  }

  &:has(> .doss-icon):not(:has(> .label)) {
    padding-inline-end: calc(var(--action-padding-inline) - var(--icon-margin));
  }

  /* ─── Small — compact footprint. Only the size-bearing properties change.
   * the dish, shadows, and press travel carry over so the keycap interpolates identically across sizes.
   * The leading-icon padding split mirrors the base rules at the small scale. */
  &.small {
    min-height: var(--small-action-height);
    gap: var(--small-action-gap);
    padding: var(--small-action-padding-block) var(--small-action-padding-inline);
    font-size: var(--small-action-text-size);

    &:has(> .doss-icon) {
      padding-inline-start: calc(var(--small-action-padding-inline) - var(--small-action-gap));
    }

    &:has(> .doss-icon):not(:has(> .label)) {
      padding-inline-end: calc(var(--small-action-padding-inline) - var(--small-action-gap));
    }
  }

  /* Press translate gated on prefers-reduced-motion: no-preference.
   * The shadow inversion still carries the press visually for users with reduced-motion preference;
   * the sub-pixel transform is purely tactile reinforcement. */
  @media (prefers-reduced-motion: no-preference) {
    &:active:not([aria-disabled="true"]) > *,
    &.loading > * {
      transform: translateY(1px);
      transition-duration: var(--action-press-duration);
    }
  }

  &.loading {
    cursor: progress;
  }

  /* ─── Filled per actionRole ─────────────────────────────────────── */

  &.primary {
    background-color: var(--primary-background);
    background-image: var(--filled-gradient);
    color: var(--primary-fill-text);
    box-shadow: var(--primary-shadow);

    &::before {
      background-image: var(--filled-dish);
      opacity: 0.64;
    }

    &:hover:not([aria-disabled="true"]) {
      background-color: var(--primary-background-hover);
    }

    &:active:not([aria-disabled="true"]),
    &.loading {
      background-color: var(--primary-background-pressed);
      box-shadow: var(--primary-shadow-pressed);
    }
  }

  &.secondary {
    background-color: var(--secondary-background);
    background-image: var(--secondary-gradient);
    color: var(--secondary-fill-text);
    box-shadow: var(--secondary-shadow);

    &::before {
      background-image: var(--secondary-dish);
      opacity: var(--secondary-dish-opacity);
    }

    &:hover:not([aria-disabled="true"]) {
      background-color: var(--secondary-background-hover);
    }

    &:active:not([aria-disabled="true"]),
    &.loading {
      background-color: var(--secondary-background-pressed);
      box-shadow: var(--secondary-shadow-pressed);
    }
  }

  &.destruction {
    background-color: var(--destruction-background);
    color: var(--destruction-fill-text);
    text-shadow: var(--destruction-label-shadow);
    box-shadow: var(--destruction-shadow);

    &::before {
      background-image: var(--filled-dish);
      opacity: 0.8;
    }

    &:hover:not([aria-disabled="true"]) {
      background-color: var(--destruction-background-hover);
    }

    &:active:not([aria-disabled="true"]),
    &.loading {
      background-color: var(--destruction-background-pressed);
      box-shadow: var(--destruction-shadow-pressed);
    }
  }

  /* ─── Ghost — flat neutral, transparent bg, subtle hover bg. No dish, no shadow, no press translate.
   * Defined AFTER filled rules in case some property leaks from a generic selector. */
  &.ghost {
    background-color: transparent;
    background-image: none;
    border-color: transparent;
    color: var(--text);
    box-shadow: none;
    text-shadow: none;

    &::before {
      display: none;
    }

    &:hover:not([aria-disabled="true"]) {
      background-color: var(--ghost-background-hover);
    }

    &:active:not([aria-disabled="true"]),
    &.loading {
      background-color: var(--ghost-background-pressed);
    }

    &.active {
      background-color: var(--ghost-background-active);
    }

    &:active:not([aria-disabled="true"]) > * {
      transform: none;
    }
  }
}
